import React, { useEffect, useMemo } from "react";
import dateFormat, { i18n } from "dateformat";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { useAppDispatch } from "@redux/store";
import { rentSelector } from "@redux/rent/selectors";
import { moscowNow } from "@/utils/date/moscowNow";
import { resetSelectedSeats, setDate, setSelctedRackets } from "@redux/rent/slice";
import { useCurrentWidth } from "@/hooks";
import { MediaBreakPoints } from "@/resources/constants";

import { Button } from "@components/index";

import styles from "./RacketRentTime.module.scss";

import { getRentDateFromStorage, getCartItemsFromStorage } from "@utils/index";
import { dayNamesForRent } from "@resources/locale";

import { RacketRentTimePropsType } from "./types";
import { IRacket, ISelctedRacket } from "@redux/rent/types";

import { DateNavigation } from "./components/DateNavigation";
import { WeekDays } from "./components/WeekDays";
import { QuantityCounter } from "./components/QuantityCounter";
import { RacketList } from "./components/RacketList";
import { RacketSkeleton } from "./components/RacketList/RacketSkeleton";
import { Modals } from "./components/Modals";
import { useQuantity } from "@hooks/useQuantity";
import { useRacketValidation } from "@hooks/useRacketValidation";
import { useAddToCart } from "./hooks/useAddToCart";
import { getRacketTypeFromStorage } from "@/utils/storage/racketType";

i18n.dayNames = dayNamesForRent;

export const RacketRentTime: React.FC<RacketRentTimePropsType> = ({ title }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const windowWidth = useCurrentWidth();
  const isMobile = windowWidth <= MediaBreakPoints.DESKTOP;


  const [isOpenModalNoSelectedRackets, setIsOpenModalNoSelectedRackets] =
    React.useState<boolean>(false);
  const [isOpenModalUnavailable, setIsOpenModalUnavailable] =
    React.useState<boolean>(false);
  const [unAvailableQuantity, setUnAvailableQuantity] = React.useState<
    Array<{ time: string; available: number }>
  >([]);

  const [racketTypePadel, setRacketTypePadel] = React.useState<boolean>(false);

  const maxCountRacket = racketTypePadel ? 25 : 12;

  const { date, selectedRackets, isLoading } = useSelector(rentSelector);
  

  const [activeRacketTime, setActiveRacketTime] = React.useState<string | null>(null);

  const { quantity, setQuantity, increase, decrease } = useQuantity(maxCountRacket);

  React.useEffect(() => {
    if (activeRacketTime && selectedRackets[activeRacketTime]) {
      const currentCount = selectedRackets[activeRacketTime].count;
      if (currentCount !== quantity) {
        setQuantity(currentCount);
      }
    } else if (!activeRacketTime && Object.keys(selectedRackets).length === 0) {
      setQuantity(1);
    }
  }, [activeRacketTime, selectedRackets, setQuantity]);

  React.useEffect(() => {
    if (activeRacketTime && selectedRackets[activeRacketTime]) {
      const currentCount = selectedRackets[activeRacketTime].count;
      if (currentCount !== quantity) {
        const updatedRackets = {
          ...selectedRackets,
          [activeRacketTime]: {
            ...selectedRackets[activeRacketTime],
            count: quantity,
          },
        };
        dispatch(setSelctedRackets(updatedRackets));
      }
    }
  }, [quantity, activeRacketTime, selectedRackets, dispatch]);

  const { hasUnavailableQuantity, unavailableQuantityList } = useRacketValidation(
    selectedRackets,
    quantity
  );

  const { handleAddToCart, handleCloseModalUnavailable } = useAddToCart(
    quantity,
    hasUnavailableQuantity,
    unavailableQuantityList,
    setIsOpenModalNoSelectedRackets,
    setIsOpenModalUnavailable,
    setUnAvailableQuantity
  );

  const arenaRacketTemp: IRacket[] = useMemo(() => {
    const array: IRacket[] = [];
    
    if (!date || isNaN(Date.parse(date))) {
      return array;
    }
    
    const baseDate = new Date(date);
    
    if (isNaN(baseDate.getTime())) {
      return array;
    }
    
    for (let i = 0; i < 17; i++) {
      const startTime = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        7 + i
      );
      const finishTime = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        8 + i
      );
      
      const hour = 7 + i;
      const timeId = `${hour < 10 ? '0' : ''}${hour}:00`;
      const selectedRacket = selectedRackets[timeId];
      
      const item = {
        racket: true,
        padel: racketTypePadel || false,
        time_id: timeId,
        startTime: startTime.toISOString(),
        finishTime: finishTime.toISOString(),
        price: 500,
        selected: !!selectedRacket,
        selectedCount: selectedRacket?.count || 1,
        available_count: maxCountRacket,
      };
      
      array.push(item);
    }
    
    return array;
  }, [date, selectedRackets, racketTypePadel, maxCountRacket]);

  React.useEffect(() => {
    const storageDate = getRentDateFromStorage();
    const racketType = getRacketTypeFromStorage();
    
    setRacketTypePadel(racketType === "padel");

    const currentDate = storageDate || dateFormat(moscowNow(), "isoDate");
    
    if (storageDate) {
      dispatch(setDate(storageDate));
    } else {
      dispatch(setDate(currentDate));
    }

    dispatch(resetSelectedSeats());
  }, [dispatch]);

  React.useEffect(() => {
    if (date) {
      setActiveRacketTime(null);

      const cartItems = getCartItemsFromStorage();
      if (cartItems && cartItems.rackets) {
        const racketsFromCart: { [key: string]: ISelctedRacket } = {};

        Object.keys(cartItems.rackets).forEach((key) => {
          const racket = cartItems.rackets[key] as ISelctedRacket;
          const isRacketPadel = !!racket.padel;
          if (racket.date === date && racket.time && isRacketPadel === racketTypePadel) {
            racketsFromCart[racket.time] = racket;
          }
        });

        if (Object.keys(racketsFromCart).length > 0) {
          dispatch(setSelctedRackets(racketsFromCart));
          // Восстанавливаем activeRacketTime и quantity из корзины
          const firstTime = Object.keys(racketsFromCart)[0];
          setActiveRacketTime(firstTime);
          const restoredCount = racketsFromCart[firstTime]?.count;
          if (restoredCount && restoredCount > 0) {
            setQuantity(restoredCount);
          }
        } else {
          dispatch(setSelctedRackets({}));
        }
      } else {
        dispatch(setSelctedRackets({}));
      }
    }
  }, [date, dispatch, racketTypePadel]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const changeSelectedRackets = React.useCallback(
    (newSelectedRackets: { [key: string]: ISelctedRacket }, clickedTime?: string | null) => {
      dispatch(setSelctedRackets(newSelectedRackets));
      if (clickedTime !== undefined) {
        if (clickedTime === null) {
          setActiveRacketTime(null);
        } else {
          setActiveRacketTime(clickedTime);
        }
      }
    },
    [dispatch]
  );

  const handleCloseModalUnavailableWithUpdate = React.useCallback(() => {
    handleCloseModalUnavailable();
    setUnAvailableQuantity([]);
  }, [handleCloseModalUnavailable]);

  return (
    <div>
      <section className="container">
        <div>
          <DateNavigation date={date} />

          <WeekDays date={date} />

          {isLoading ? (
            <RacketSkeleton />
          ) : (
            <RacketList
              rackets={arenaRacketTemp}
              date={date}
              quantity={quantity}
              activeRacketTime={activeRacketTime}
              onRacketSelect={changeSelectedRackets}
            />
          )}

          <div className={styles.cartActions}>
            {
              activeRacketTime && selectedRackets[activeRacketTime] && (
                <QuantityCounter
                  quantity={quantity}
                  onIncrease={increase}
                  onDecrease={decrease}
                  maxQuantity={maxCountRacket}
                />
              )
            }
            {(!isMobile || Object.keys(selectedRackets).length > 0) && (
            <Button newStyles={styles.button} onClickFunc={handleAddToCart}>
              <span>В корзину</span>
            </Button>
            )}
          </div>
        </div>

        <Modals
          isOpenModalNoSelectedRackets={isOpenModalNoSelectedRackets}
          setIsOpenModalNoSelectedRackets={setIsOpenModalNoSelectedRackets}
          isOpenModalUnavailable={isOpenModalUnavailable}
          setIsOpenModalUnavailable={setIsOpenModalUnavailable}
          unavailableQuantity={unAvailableQuantity}
          onCloseUnavailable={handleCloseModalUnavailableWithUpdate}
        />
      </section>
    </div>
  );
};
